import { useParams, Link, useLocation  } from "react-router-dom";
import { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot } from "firebase/firestore";
import ProductCard from "../components/ProductCard";
import "../styles/CategoryProducts.css";

export default function CategoryProducts() {
  const { cid } = useParams();
  const location = useLocation();
  const [categoryName, setCategoryName] = useState(location.state?.categoryName || "");


  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState("newest");

  // 🔥 FETCH PRODUCTS BY CATEGORY
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "products"), (snap) => {
      const data = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter by category ID
      const filtered = data.filter(
        (product) => String(product.categoryId) === cid
      );

      // Sort products
      const sorted = sortProducts(filtered, sortBy);
      setProducts(sorted);
      setIsLoading(false);

      // Extract category name from first product or set default
      if (filtered.length > 0 && filtered[0].categoryName) {
        setCategoryName(filtered[0].categoryName);
      }
    });

    return () => unsubscribe();
  }, [cid, sortBy]);

  // SORT PRODUCTS FUNCTION
  const sortProducts = (productsToSort, sortOption) => {
    const sorted = [...productsToSort];

    switch (sortOption) {
      case "price-low":
        return sorted.sort((a, b) => a.price - b.price);
      case "price-high":
        return sorted.sort((a, b) => b.price - a.price);
      case "name-a-z":
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case "name-z-a":
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      default:
        return sorted;
    }
  };

  // 📦 LOADING STATE
  if (isLoading) {
    return (
      <div className="category-products__wrapper">
        <div className="category-products__loading-container">
          <div className="category-products__spinner"></div>
          <p className="category-products__loading-text">
            Loading products...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="category-products__wrapper">
      <div className="category-products__container">
        {/* HEADER SECTION */}
        <div className="category-products__header">
          <div className="category-products__header-content">
            <h1 className="category-products__title">
              {categoryName || "Products"}
            </h1>
            <p className="category-products__subtitle">
              {products.length} {products.length === 1 ? "product" : "products"}{" "}
              available
            </p>
          </div>

          {/* BREADCRUMB */}
          <nav className="category-products__breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="category-products__breadcrumb-link">
              Home
            </Link>
            <span className="category-products__breadcrumb-separator">/</span>
            <span className="category-products__breadcrumb-current">
              {categoryName || "Products"}
            </span>
          </nav>
        </div>

        {/* FILTER & SORT SECTION */}
        {products.length > 0 && (
          <div className="category-products__controls">
            <div className="category-products__sort">
              <label htmlFor="sort-select" className="category-products__sort-label">
                Sort by:
              </label>
              <select
                id="sort-select"
                className="category-products__sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort products"
              >
                <option value="newest">Newest First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name-a-z">Name: A to Z</option>
                <option value="name-z-a">Name: Z to A</option>
              </select>
            </div>
          </div>
        )}

        {/* PRODUCTS GRID */}
        <div className="category-products__content">
          {products.length > 0 ? (
            <div className="category-products__grid">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="category-products__empty">
              <div className="category-products__empty-icon">📦</div>
              <h2 className="category-products__empty-title">
                No Products Found
              </h2>
              <p className="category-products__empty-message">
                Sorry, we couldn't find any products in this category. Please
                try another category.
              </p>
              <Link to="/" className="category-products__empty-btn">
                ← Back to Home
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}